// DS: MO3.4.3 (collectible)
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import MediaIndicator from '@wix/da-ds/pkg/thumbs/MediaIndicator';
import BoostedIndicator from '@wix/da-ds/pkg/thumbs/BoostedIndicator';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { Props as DeviationShopCardProps } from '../DeviationShopCard/DeviationShopCard';
import DeviationLink from '../../Duperbrowse/DeviationLink';
import { getVideoDuration, isVideoOrGif, getDeviationPrice } from '../../utils/deviations';
import DeviationThumbIndicators, {
  AllowedIndicators,
} from '../../DeviationViews/Thumb/legos/DeviationThumbIndicators';
import DeviationDumbView from '../../DeviationViews/Dumbo';
import UserAvatar from '../../User/Avatar/UserAvatar';
import UserLink from '../../User/Link/UserLink';

import { isBoostedDeviation, isMultiMediaDeviation } from '../../DeviationViews/_helpers';

import s from './AdoptableShopCard.exclusives_newstyle.scss';

export const CARD_HEIGHT = 380;
export const COVER_IMG_HEIGHT = 270;
export const OWNER_AVATAR_SIZE = 24;

export interface Props extends Pick<
  DeviationShopCardProps,
  | 'detailsRightSlot'
  | 'footerSlot'
  | 'allowedIndicators'
  | 'withAuthorDetails'
  | 'withOwnerDetails'
  | 'deviation'
  | 'width'
  | 'overrideHref'
  | 'extraBiData'
> {
  hidePrice?: boolean;
  hideSold?: boolean;
}

const AdoptableShopCardV2: FC<Props> = ({
  deviation,
  width,
  overrideHref,
  extraBiData,
  allowedIndicators,
  withOwnerDetails,
}) => {
  const { t } = useTranslation();
  const height = CARD_HEIGHT;
  const dumboWidth = width - parseInt(s['border-width']) * 2;
  const { dollarsPrice, pointsPrice } = getDeviationPrice(deviation);
  const owner = deviation.adoptable?.owner;
  const formattedDollarsPrice = `$${parseInt(dollarsPrice ?? '0')}`;
  const isBoosted = isBoostedDeviation(deviation);
  const isMultiVideoFlagEnabled = useFlag('multi_video');
  const isMultiMedia = isMultiMediaDeviation(deviation, isMultiVideoFlagEnabled);

  const { title, author } = deviation;

  // todo: there is an edge case where an author -buys back- a work that we
  // may want to handle here at some point
  const isNewOwner = owner && author?.userId && owner.userId !== author.userId;

  return (
    <div className={s['root']} style={{ width, height }}>
      <DeviationLink
        className={s['link']}
        deviation={deviation}
        overrideHref={overrideHref}
        extraBiData={extraBiData}
        canLinkToStash
        aria-label={t('shop.thumb.a11y.shopLink', {
          username: owner?.username ?? deviation.author?.username,
          deviationName: title,
          dollarPrice: formattedDollarsPrice,
          count: pointsPrice,
        })}
      />
      <div aria-hidden="true" className={s['content']}>
        <div className={s['indicators-slot']}>
          {allowedIndicators !== AllowedIndicators.All && (
            <DeviationThumbIndicators
              className={s['indicators']}
              deviation={deviation}
              allowedIndicators={allowedIndicators}
            />
          )}
          <div className={s['indicators-container']}>
            {isBoosted && isMultiMedia && <BoostedIndicator className={s['boosted']} />}
            <MediaIndicator
              className={s['media-indicator']}
              type={isVideoOrGif(deviation)}
              duration={getVideoDuration(deviation)}
              isMultiImage={isMultiMedia}
            />
          </div>
        </div>
        <div
          className={s['overlay-container']}
          style={{ width: dumboWidth, height: COVER_IMG_HEIGHT }}
        >
          <DeviationDumbView
            className={s['cover-image']}
            deviation={deviation}
            useCoverMode
            width={dumboWidth}
            height={COVER_IMG_HEIGHT}
            videoIconSize={IconSize.GIANT}
          />
          {withOwnerDetails && isNewOwner && (
            <div className={s['owner-overlay']}>
              <div className={s['owner-details']}>
                <UserAvatar user={owner} size={OWNER_AVATAR_SIZE} />
                {t('shop.thumb.adoptable.ownedBy')}
                <UserLink user={owner} className={s['owner-link']} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
AdoptableShopCardV2.displayName = 'AdoptableShopCard';

export default AdoptableShopCardV2;
